import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export default function usePageInteractions() {
  const { pathname } = useLocation();

  useEffect(() => {
    // 0. Dynamic SEO Page Titles
    const baseTitle = "Azaziya International";
    let suffix = "Branding, Web, SEO & Creative Agency in Dubai, UAE";

    if (pathname.includes('/about')) {
      suffix = "About Our Creative Agency";
    } else if (pathname.includes('/services/branding')) {
      suffix = "Branding & Visual Identity Services";
    } else if (pathname.includes('/services/digital-marketing')) {
      suffix = "Digital & Social Media Marketing";
    } else if (pathname.includes('/services/web-development')) {
      suffix = "Premium Custom Web Development";
    } else if (pathname.includes('/services/3d-event-design')) {
      suffix = "Immersive 3D Event & Exhibition Stand Design";
    } else if (pathname.includes('/services/video-animation')) {
      suffix = "2D/3D Motion Graphics & Animation Solutions";
    } else if (pathname.includes('/services/seo-ai-solutions')) {
      suffix = "SEO, AEO & AI Automation Solutions";
    } else if (pathname.includes('/services')) {
      suffix = "Our Creative & Digital Services";
    } else if (pathname.includes('/portfolio/3d-event-exhibition')) {
      suffix = "3D exhibition & Event Design Portfolio";
    } else if (pathname.includes('/portfolio/web-development')) {
      suffix = "Web Development Portfolio";
    } else if (pathname.includes('/portfolio/branding')) {
      suffix = "Branding & Visual Identity Portfolio";
    } else if (pathname.includes('/portfolio/video-animation')) {
      suffix = "Video & Motion Animation Portfolio";
    } else if (pathname.includes('/portfolio/social-media-marketing')) {
      suffix = "Social Media Marketing Portfolio";
    } else if (pathname.includes('/portfolio/seo')) {
      suffix = "Search Engine Optimization Portfolio";
    } else if (pathname.includes('/portfolio')) {
      suffix = "Our Creative & Digital Portfolio";
    } else if (pathname.includes('/team')) {
      suffix = "Meet Our Team of Design & Tech Experts";
    } else if (pathname.includes('/blog/branding-vs-logo')) {
      suffix = "Branding vs. Logo Design - What's the Difference?";
    } else if (pathname.includes('/blog/local-seo-dubai')) {
      suffix = "Local SEO Dubai - What Actually Moves the Needle";
    } else if (pathname.includes('/blog/website-speed')) {
      suffix = "How Website Speed Impacts Conversion Rates";
    } else if (pathname.includes('/blog/social-media-roi')) {
      suffix = "Measuring Social Media ROI for B2B Brands";
    } else if (pathname.includes('/blog/exhibition-stand-mistakes')) {
      suffix = "Exhibition Stand Design Mistakes to Avoid";
    } else if (pathname.includes('/blog/animation-conversions')) {
      suffix = "How Animation Increases Landing Page Conversions";
    } else if (pathname.includes('/blog')) {
      suffix = "Insights & Strategy Blog";
    } else if (pathname.includes('/contact')) {
      suffix = "Get a Free Strategy Session";
    } else if (pathname.includes('/terms')) {
      suffix = "Terms & Conditions & Privacy Policy";
    }

    document.title = `${baseTitle} | ${suffix}`;

    // 1. Scroll Reveal (Fade/Slide up on entering viewport)
    const revealEls = document.querySelectorAll('.reveal');
    let io;
    if ('IntersectionObserver' in window && revealEls.length) {
      io = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
      revealEls.forEach(el => io.observe(el));
    } else {
      revealEls.forEach(el => el.classList.add('in'));
    }

    // 2. FAQ Accordion Click Handler
    const faqItems = document.querySelectorAll('.faq-item');
    const faqCleanups = [];

    faqItems.forEach(item => {
      const q = item.querySelector('.faq-q');
      const a = item.querySelector('.faq-a');
      if (!q || !a) return;

      const clickHandler = () => {
        const isOpen = item.classList.contains('open');
        const list = item.closest('.faq-list');
        if (list) {
          list.querySelectorAll('.faq-item').forEach(el => {
            el.classList.remove('open');
            const innerA = el.querySelector('.faq-a');
            if (innerA) innerA.style.maxHeight = null;
          });
        }
        if (!isOpen) {
          item.classList.add('open');
          a.style.maxHeight = a.scrollHeight + 'px';
        }
      };

      q.addEventListener('click', clickHandler);
      faqCleanups.push({ q, clickHandler });
    });

    // 3. Animated stat counters (Tick up when visible)
    const counters = document.querySelectorAll('[data-count]');
    let co;
    if (counters.length) {
      co = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const target = parseFloat(el.getAttribute('data-count'));
          const suffix = el.getAttribute('data-suffix') || '';
          const dur = 1400;
          const start = performance.now();
          function tick(now) {
            const p = Math.min((now - start) / dur, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const val = target < 10 ? (target * eased).toFixed(1) : Math.floor(target * eased);
            el.textContent = val + suffix;
            if (p < 1) requestAnimationFrame(tick);
          }
          requestAnimationFrame(tick);
          co.unobserve(el);
        });
      }, { threshold: 0.2 });
      counters.forEach(el => co.observe(el));
    }

    // 4. Service card tilt / radial light hover effect (desktop only)
    const isTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (window.innerWidth < 768);
    const cards = document.querySelectorAll('.card, .cta-band, .portfolio-card');
    const cardCleanups = [];
    if (!isTouch) {
      cards.forEach(card => {
        const handleMouseMove = (e) => {
          const r = card.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width;
          const y = (e.clientY - r.top) / r.height;
          card.style.background = `radial-gradient(circle at ${x * 100}% ${y * 100}%, var(--bg-elev), var(--bg-alt) 70%)`;
        };
        const handleMouseLeave = () => {
          card.style.background = '';
        };
        card.addEventListener('mousemove', handleMouseMove, { passive: true });
        card.addEventListener('mouseleave', handleMouseLeave, { passive: true });
        cardCleanups.push({ card, handleMouseMove, handleMouseLeave });
      });
    }

    // Cleanup all listeners and observers on route switch
    return () => {
      if (io) {
        revealEls.forEach(el => {
          try { io.unobserve(el); } catch (err) { }
        });
      }
      if (co) {
        counters.forEach(el => {
          try { co.unobserve(el); } catch (err) { }
        });
      }
      faqCleanups.forEach(({ q, clickHandler }) => {
        q.removeEventListener('click', clickHandler);
      });
      cardCleanups.forEach(({ card, handleMouseMove, handleMouseLeave }) => {
        card.removeEventListener('mousemove', handleMouseMove);
        card.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [pathname]);
}
